import { useCallback, useEffect, useState } from 'preact/hooks';
/** A unit counts as in view once it has been shown for 1 continuous second. */
const IN_VIEW_MS = 1000;
/** An embedded unit is only shown while at least 50% of it is in the viewport. */
const EMBEDDED_SDK_IN_VIEW_RATIO = 0.5;
/**
 * Calls `onShown` once `el` has been shown for IN_VIEW_MS without a break,
 * with the tab visible. With `minInViewRatio`, `el` only counts as shown while
 * at least that fraction of it is in the viewport (the embedded SDK); without
 * it, `el` counts as shown for as long as it is mounted (a popup, which always
 * opens on screen). Any break restarts the full second. Returns a cleanup that
 * cancels the pending timer and stops watching.
 */
function whenUnitShownFor1s(el, minInViewRatio, onShown) {
    const doc = el.ownerDocument;
    let isOnScreen = minInViewRatio === undefined;
    let timer = null;
    const cancelTimer = () => {
        if (timer !== null)
            clearTimeout(timer);
        timer = null;
    };
    const stop = () => {
        cancelTimer();
        observer?.disconnect();
        doc.removeEventListener('visibilitychange', update);
    };
    function update() {
        if (!isOnScreen || doc.visibilityState === 'hidden') {
            cancelTimer();
            return;
        }
        if (timer !== null)
            return;
        timer = setTimeout(() => {
            stop();
            onShown();
        }, IN_VIEW_MS);
    }
    const observer = minInViewRatio === undefined
        ? null
        : new IntersectionObserver(entries => {
            const entry = entries[entries.length - 1];
            isOnScreen =
                entry !== undefined &&
                    entry.isIntersecting &&
                    entry.intersectionRatio >= minInViewRatio;
            update();
        }, { threshold: minInViewRatio });
    observer?.observe(el);
    doc.addEventListener('visibilitychange', update);
    update();
    return stop;
}
/**
 * Reports whether the element given to the returned ref has been shown for 1
 * continuous second: a popup counts from the moment it is open, the embedded
 * SDK only while at least 50% of it is in the viewport, and a hidden tab never
 * counts. Unmounting the element (the unit closing) or changing `resetKey`
 * (the offer changing) before then cancels the count. Once true, the result
 * stays true until `resetKey` changes.
 */
export function useIsInView({ isEmbedded = false, resetKey, } = {}) {
    const [el, setEl] = useState(null);
    const [shown, setShown] = useState(null);
    const isShown = shown !== null && shown.key === resetKey;
    useEffect(() => {
        setShown(null);
    }, [resetKey]);
    useEffect(() => {
        if (!el || isShown)
            return;
        return whenUnitShownFor1s(el, isEmbedded ? EMBEDDED_SDK_IN_VIEW_RATIO : undefined, () => setShown({ key: resetKey }));
    }, [el, isEmbedded, resetKey, isShown]);
    const ref = useCallback((node) => setEl(node), []);
    return [isShown, ref];
}
